import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import { useTheme } from "../../../context/ThemeContext";
import { brand } from "../../../config/theme";
import { buildThemeClasses } from "../theme";
import { OriginCell } from "../components/ImportedFromTag";
import { CompanyLogo } from "../components/CompanyLogo";
import {
  FILTER_ALL,
  FilterSelect,
  countBy,
  matchesFilter,
  productFilterKey,
  productFilterOptions,
  sourceFilterKey,
  sourceFilterOptions,
  stageFilterOptions,
} from "../components/FilterSelect";
import {
  fetchCompanies,
  getCompany,
  updateCompany,
  deleteCompany,
  addCompanyPerson,
  companyPhone,
  companyLocation,
  companyWebsiteUrl,
  companyWebsiteLabel,
  type Company,
  type CompanyDetail,
  type CompanyStage,
} from "../services/companiesService";
import { LEAD_PRODUCTS, type Lead } from "../services/leadsService";
import { hasWhatsappLine, leadPhone } from "../services/leadPhone";
import {
  BILLING_META,
  apiErrorMessage,
  billingLabel,
  fetchCompaniesUniverse,
  linkCompanyToCrm,
  type PlatformCommercial,
  type UniverseTotals,
} from "../services/companyUniverseService";
import { STAGE_BY_ID, type DealStage } from "../services/dealsService";
import { ROLE_CATALOG } from "../services/contactRoles";

// ===========================================================================
// Empresas — LIVE directory. Companies are created automatically the first
// time a lead arrives with a given company name (see the backend
// companyService + onLeadCreated trigger). Each row shows how many leads in
// our DB are assigned to that company. Console users can triage stage / edit
// details / delete.
//
// Universo único (#1071): la columna «Plataforma» muestra la compañía de la
// plataforma vinculada (su estado y la etapa de su negocio), y abajo se listan
// las compañías de la plataforma que aún no tienen Empresa del CRM, para
// crearla o vincularla. Solo lo ve un administrador de plataforma.
// ===========================================================================

const POLL_MS = 30000;

const STAGE_META: Record<CompanyStage, { label: string; tone: string }> = {
  prospect: { label: "Prospecto", tone: "bg-sky-500/15 text-sky-500" },
  customer: { label: "Cliente", tone: "bg-emerald-500/15 text-emerald-500" },
  inactive: { label: "Inactivo", tone: "bg-gray-400/15 text-gray-500" },
};

const STAGE_ORDER: CompanyStage[] = ["prospect", "customer", "inactive"];

// Visual style per product — mirrors the Leads inbox badges.
const PRODUCT_TONE: Record<string, string> = {
  "Coding Cloud": "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  Wayak: "bg-indigo-500/15 text-indigo-500",
  Nuvex: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  Movik: "bg-orange-500/15 text-orange-600 dark:text-orange-400",
};

export default function EmpresasLive() {
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [productFilter, setProductFilter] = useState(FILTER_ALL);
  const [sourceFilter, setSourceFilter] = useState(FILTER_ALL);
  const [stageFilter, setStageFilter] = useState(FILTER_ALL);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [addPersonFor, setAddPersonFor] = useState<Company | null>(null);
  const [detailFor, setDetailFor] = useState<Company | null>(null);
  const [universe, setUniverse] = useState<{ platform: PlatformCommercial[]; totals: UniverseTotals } | null>(null);
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const firstLoad = useRef(true);
  const withUniverseRef = useRef(false);

  const load = useCallback(async () => {
    if (firstLoad.current) setLoading(true);
    setError(null);
    try {
      // El universo (compañías + Empresas + negocios) es caro: se pide al abrir
      // y al pulsar «Actualizar», no en cada sondeo de 30 s.
      const withUniverse = firstLoad.current || withUniverseRef.current;
      withUniverseRef.current = false;
      const [page, uni] = await Promise.all([
        fetchCompanies({ limit: 300 }),
        withUniverse ? fetchCompaniesUniverse().catch(() => null) : Promise.resolve(undefined),
      ]);
      setCompanies(page.data);
      if (uni !== undefined) setUniverse(uni);
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { message?: string } }; message?: string };
      // 404 = endpoint not deployed yet → just show the empty state.
      if (err.response?.status === 404) setCompanies([]);
      else setError(err.response?.data?.message || err.message || "No se pudieron cargar las empresas");
    } finally {
      setLoading(false);
      firstLoad.current = false;
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const totalLeads = useMemo(
    () => companies.reduce((sum, c) => sum + (c.leadsCount ?? 0), 0),
    [companies],
  );

  // Empresa del CRM → su compañía de la plataforma.
  const platformByCrm = useMemo(() => {
    const map = new Map<string, PlatformCommercial>();
    for (const p of universe?.platform ?? []) if (p.crmCompanyId) map.set(p.crmCompanyId, p);
    return map;
  }, [universe]);

  const unlinkedPlatform = useMemo(
    () => (universe?.platform ?? []).filter((p) => !p.crmCompanyId && p.billingClass !== "ended").sort((a, b) => Number(b.needsLink) - Number(a.needsLink)),
    [universe],
  );

  /** Empresas del CRM libres (sin compañía de plataforma) para «Vincular a…». */
  const freeCrmCompanies = useMemo(
    () => companies.filter((c) => !c.platformCompanyId && !platformByCrm.has(c.id)).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [companies, platformByCrm],
  );

  const linkPlatform = async (p: PlatformCommercial, target: { crmCompanyId: string } | { create: true }) => {
    setLinkingId(p.id);
    try {
      await linkCompanyToCrm(p.id, target);
      withUniverseRef.current = true;
      await load();
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se pudo vincular", text: apiErrorMessage(e, "Intenta de nuevo"), confirmButtonColor: brand.primary });
    } finally {
      setLinkingId(null);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return companies.filter((c) => {
      if (!matchesFilter(productFilter, productFilterKey(c.product))) return false;
      if (!matchesFilter(sourceFilter, sourceFilterKey(c.source))) return false;
      if (!matchesFilter(stageFilter, c.stage)) return false;
      if (!q) return true;
      return [c.name, c.industry, c.city, c.size, c.prospect?.state, c.prospect?.country]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [companies, query, productFilter, sourceFilter, stageFilter]);

  const productOptions = useMemo(() => productFilterOptions(companies), [companies]);
  const sourceOptions = useMemo(() => sourceFilterOptions(companies), [companies]);
  const stageOptions = useMemo(
    () => stageFilterOptions(STAGE_ORDER, STAGE_META, countBy(companies, (c) => c.stage)),
    [companies],
  );

  const changeStage = async (company: Company, stage: CompanyStage) => {
    setSavingId(company.id);
    setCompanies((prev) => prev.map((c) => (c.id === company.id ? { ...c, stage } : c)));
    try {
      await updateCompany(company.id, { stage });
    } catch {
      await load();
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo actualizar la etapa", confirmButtonColor: brand.primary });
    } finally {
      setSavingId(null);
    }
  };

  const changeProduct = async (company: Company, product: string) => {
    const value = product || null;
    setSavingId(company.id);
    setCompanies((prev) => prev.map((c) => (c.id === company.id ? { ...c, product: value } : c)));
    try {
      await updateCompany(company.id, { product: value });
    } catch {
      await load();
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo actualizar el producto", confirmButtonColor: brand.primary });
    } finally {
      setSavingId(null);
    }
  };

  const handleAddPerson = async (
    company: Company,
    input: { name: string; whatsapp?: string; email?: string; role?: string },
  ) => {
    await addCompanyPerson(company.id, input);
    setAddPersonFor(null);
    // Reflect the new lead in the company's count immediately.
    setCompanies((prev) =>
      prev.map((c) => (c.id === company.id ? { ...c, leadsCount: (c.leadsCount ?? 0) + 1 } : c)),
    );
    Swal.fire({
      icon: "success",
      title: "Persona agregada",
      text: `${input.name} ahora es un lead de ${company.name}.`,
      confirmButtonColor: brand.primary,
    });
  };

  const removeCompany = async (company: Company) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Eliminar empresa",
      text: `¿Eliminar "${company.name}"? Los leads asignados no se borran.`,
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#DC2626",
    });
    if (!res.isConfirmed) return;
    setCompanies((prev) => prev.filter((c) => c.id !== company.id));
    try {
      await deleteCompany(company.id);
    } catch {
      await load();
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo eliminar la empresa", confirmButtonColor: brand.primary });
    }
  };

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-building-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Empresas</h1>
              <p className={`text-sm ${tc.textSecondary}`}>
                Organizaciones de tu CRM · {companies.length} empresas · {totalLeads} leads asignados
                {universe && (
                  <>
                    {" "}· {universe.totals.linked} de {universe.totals.platform} compañías de la plataforma vinculadas
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${tc.border} ${tc.cardBg}`}>
              <i className={`ri-search-line ${tc.textSecondary}`} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar empresas..."
                className={`bg-transparent text-sm outline-none ${tc.text}`}
              />
            </div>
            <button
              onClick={() => {
                withUniverseRef.current = true;
                load();
              }}
              className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-400"
            >
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} />
              <span className="whitespace-nowrap">Actualizar</span>
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <FilterSelect label="Producto" value={productFilter} onChange={setProductFilter} allCount={companies.length} options={productOptions} isDarkMode={isDarkMode} />
          <FilterSelect label="Origen" value={sourceFilter} onChange={setSourceFilter} allCount={companies.length} options={sourceOptions} isDarkMode={isDarkMode} />
          <FilterSelect label="Etapa" value={stageFilter} onChange={setStageFilter} allLabel="Todas" allCount={companies.length} options={stageOptions} isDarkMode={isDarkMode} />
        </div>

        {/* Table */}
        <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
          {loading ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" />
              Cargando empresas...
            </div>
          ) : error ? (
            <div className="p-10 text-center">
              <i className="ri-error-warning-line mb-2 text-3xl text-red-500" />
              <p className="text-sm text-red-500">{error}</p>
              <button onClick={load} className="mt-3 rounded-lg bg-primary-500 px-4 py-2 text-sm text-white hover:bg-primary-400">
                Reintentar
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className={`p-10 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-building-line mb-2 block text-3xl" />
              {companies.length > 0
                ? "Ninguna empresa coincide con la búsqueda o los filtros."
                : "Aún no hay empresas. Se crean automáticamente cuando llega un lead con nombre de empresa."}
            </div>
          ) : (
            <div className="w-full overflow-x-auto p-4">
              <table className="w-full overflow-hidden rounded-xl text-xs shadow-sm">
                <thead className="bg-primary-500 text-white">
                  <tr>
                    <th className="p-3 text-left font-bold">Empresa</th>
                    {universe && <th className="p-3 text-left font-bold">Plataforma</th>}
                    <th className="p-3 text-left font-bold">Producto</th>
                    <th className="p-3 text-left font-bold">Origen</th>
                    <th className="p-3 text-left font-bold">Industria</th>
                    <th className="p-3 text-left font-bold">Teléfono</th>
                    <th className="p-3 text-left font-bold">Estado / País</th>
                    <th className="p-3 text-center font-bold">Leads</th>
                    <th className="p-3 text-center font-bold">Contactos</th>
                    <th className="p-3 text-center font-bold">Etapa</th>
                    <th className="p-3 text-center font-bold">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, i) => (
                    <tr
                      key={c.id}
                      className={
                        isDarkMode
                          ? i % 2 === 0 ? "bg-[#232323] text-white" : "bg-[#1C1C1C] text-white"
                          : i % 2 === 0 ? "bg-gray-50 text-gray-900" : "bg-white text-gray-900"
                      }
                    >
                      <td className="p-2 font-medium">
                        <span className="inline-flex items-center gap-2">
                        <CompanyLogo
                          name={c.name}
                          logoUrl={c.logoUrl}
                          domain={c.domain}
                          website={c.website ?? c.prospect?.website}
                        />
                        {companyWebsiteUrl(c) ? (
                          <a
                            href={companyWebsiteUrl(c)!}
                            target="_blank"
                            rel="noreferrer"
                            title={companyWebsiteLabel(c) ?? "Abrir sitio web"}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-primary-500 hover:underline"
                          >
                            {c.name}
                            <i className="ri-external-link-line text-[11px] opacity-70" />
                          </a>
                        ) : (
                          c.name
                        )}
                        </span>
                      </td>
                      {universe && (
                        <td className="p-2">
                          <PlatformCell platform={platformByCrm.get(c.id) ?? null} />
                        </td>
                      )}
                      <td className="p-2">
                        <select
                          value={c.product ?? ""}
                          disabled={savingId === c.id}
                          onChange={(e) => changeProduct(c, e.target.value)}
                          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold outline-none ${
                            c.product ? PRODUCT_TONE[c.product] ?? "bg-gray-400/15 text-gray-500" : "bg-gray-400/15 text-gray-500"
                          } ${isDarkMode ? "[&>option]:bg-[#232323] [&>option]:text-white" : ""}`}
                        >
                          <option value="">—</option>
                          {LEAD_PRODUCTS.map((p) => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-2">
                        <OriginCell source={c.source} />
                      </td>
                      <td className="p-2">{c.industry ?? "—"}</td>
                      <td className="p-2">{companyPhone(c) ?? "—"}</td>
                      <td className="p-2">{companyLocation(c) ?? "—"}</td>
                      <td className="p-2 text-center">
                        <span className="inline-flex min-w-[28px] items-center justify-center rounded-full bg-primary-500/15 px-2 py-0.5 font-semibold text-primary-500">
                          {c.leadsCount ?? 0}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <span className="inline-flex min-w-[28px] items-center justify-center rounded-full bg-gray-400/15 px-2 py-0.5 font-semibold text-gray-500">
                          {c.contactsCount ?? 0}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        <select
                          value={c.stage}
                          disabled={savingId === c.id}
                          onChange={(e) => changeStage(c, e.target.value as CompanyStage)}
                          className={`rounded-md border px-2 py-1 text-xs outline-none ${
                            isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
                          }`}
                        >
                          {STAGE_ORDER.map((s) => (
                            <option key={s} value={s}>{STAGE_META[s].label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-2">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            title="Ver detalle de la empresa"
                            onClick={() => setDetailFor(c)}
                            className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
                          >
                            <i className="ri-eye-line" />
                          </button>
                          <button
                            title="Agregar persona (crea un lead)"
                            onClick={() => setAddPersonFor(c)}
                            className="rounded-md p-1.5 text-emerald-500 hover:bg-emerald-500/10"
                          >
                            <i className="ri-user-add-line" />
                          </button>
                          <button
                            title="Eliminar"
                            onClick={() => removeCompany(c)}
                            className="rounded-md p-1.5 text-red-500 hover:bg-red-500/10"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {universe && unlinkedPlatform.length > 0 && (
          <div className={`mt-6 rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
            <h2 className={`mb-1 text-sm font-bold ${tc.text}`}>
              <i className="ri-links-line mr-1 text-primary-500" />
              Compañías de la plataforma sin Empresa del CRM ({unlinkedPlatform.length})
            </h2>
            <p className={`mb-3 text-xs ${tc.textSecondary}`}>
              Una compañía solo puede ser Facturable si está vinculada a su Empresa del CRM; al vincularla se
              crea o se reutiliza su negocio en Negocios.
            </p>
            <ul className="divide-y divide-gray-400/20">
              {unlinkedPlatform.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-xs">
                  <span className={`flex items-center gap-2 font-medium ${tc.text}`}>
                    {p.name || p.id}
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${BILLING_META[p.billingClass].tone}`}>
                      {billingLabel(p)}
                    </span>
                    {p.needsLink && (
                      <span className="text-[11px] text-amber-600 dark:text-amber-400" title="Quedó Facturable antes de la regla: vincúlala">
                        <i className="ri-error-warning-line mr-0.5" />
                        Facturable sin vínculo
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2">
                    <button
                      disabled={linkingId === p.id}
                      onClick={() => linkPlatform(p, { create: true })}
                      className="rounded-md bg-primary-500 px-3 py-1 text-white hover:bg-primary-400 disabled:opacity-50"
                    >
                      Crear en el CRM
                    </button>
                    <select
                      aria-label={`Vincular ${p.name ?? p.id} a una Empresa del CRM`}
                      value=""
                      disabled={linkingId === p.id || freeCrmCompanies.length === 0}
                      onChange={(e) => e.target.value && linkPlatform(p, { crmCompanyId: e.target.value })}
                      className={`max-w-[220px] rounded-md border px-2 py-1 outline-none ${
                        isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
                      }`}
                    >
                      <option value="">Vincular a…</option>
                      {freeCrmCompanies.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <p className={`mt-3 text-xs ${tc.textSecondary}`}>
          <i className="ri-information-line mr-1" />
          Las empresas se crean automáticamente a partir de los leads y de la Prospección web.
          Usa <i className="ri-user-add-line" /> para agregar una persona: se convierte en un lead
          (con el producto de la empresa) y aparece en la Bandeja, el Pipeline y las llamadas.
        </p>
      </div>

      {detailFor && (
        <CompanyDetailModal
          company={detailFor}
          tc={tc}
          isDarkMode={isDarkMode}
          onClose={() => setDetailFor(null)}
          onAddPerson={(c) => {
            setDetailFor(null);
            setAddPersonFor(c);
          }}
        />
      )}

      {addPersonFor && (
        <AddPersonModal
          company={addPersonFor}
          tc={tc}
          isDarkMode={isDarkMode}
          onClose={() => setAddPersonFor(null)}
          onSubmit={handleAddPerson}
        />
      )}
    </div>
  );
}

// Small modal to add a named person to a company (which creates a lead).
function AddPersonModal({
  company,
  tc,
  isDarkMode,
  onClose,
  onSubmit,
}: {
  company: Company;
  tc: ReturnType<typeof buildThemeClasses>;
  isDarkMode: boolean;
  onClose: () => void;
  onSubmit: (
    company: Company,
    input: { name: string; whatsapp?: string; email?: string; role?: string },
  ) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState(companyPhone(company) ?? "");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm outline-none ${
    isDarkMode ? "border-dark-border bg-[#232323] text-white" : "border-gray-300 bg-white text-gray-900"
  }`;

  const submit = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit(company, {
        name: name.trim(),
        whatsapp: whatsapp.trim() || undefined,
        email: email.trim() || undefined,
        role: role.trim() || undefined,
      });
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err.response?.data?.message || err.message || "No se pudo agregar la persona");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className={`w-full max-w-md rounded-2xl border p-5 ${tc.cardBorder} ${tc.cardBg}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-500">
            <i className="ri-user-add-line text-lg" />
          </span>
          <div>
            <h3 className={`text-base font-bold ${tc.text}`}>Agregar persona</h3>
            <p className={`text-xs ${tc.textSecondary}`}>{company.name} · se creará un lead</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Nombre *</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="Nombre de la persona"
              className={inputCls}
            />
          </div>
          <div>
            <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>WhatsApp / Teléfono</label>
            <input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+52 …" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Correo</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="correo@…" className={inputCls} />
            </div>
            <div>
              <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>Cargo</label>
              <select aria-label="Cargo" value={role} onChange={(e) => setRole(e.target.value)} className={inputCls}>
                <option value="">Sin especificar</option>
                {ROLE_CATALOG.map((area) => (
                  <optgroup key={area.id} label={area.label}>
                    {area.roles.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className={`rounded-lg border px-4 py-2 text-sm ${tc.border} ${tc.text}`}>
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={!name.trim() || saving}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-600 disabled:opacity-50"
          >
            <i className={saving ? "ri-loader-4-line animate-spin" : "ri-user-add-line"} />
            Crear lead
          </button>
        </div>
      </div>
    </div>
  );
}

// Read-only detail panel for a company: full company data + the contact people
// (leads) we have registered against it. Fetches the single-company endpoint on
// open so the linked leads are always fresh.
function CompanyDetailModal({
  company,
  tc,
  isDarkMode,
  onClose,
  onAddPerson,
}: {
  company: Company;
  tc: ReturnType<typeof buildThemeClasses>;
  isDarkMode: boolean;
  onClose: () => void;
  onAddPerson: (company: Company) => void;
}) {
  const [detail, setDetail] = useState<CompanyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    getCompany(company.id)
      .then((d) => alive && setDetail(d))
      .catch((e: { response?: { data?: { message?: string } }; message?: string }) => {
        if (alive) setError(e.response?.data?.message || e.message || "No se pudo cargar la empresa");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [company.id]);

  // While the detail loads, fall back to the row data we already have.
  const c = detail ?? company;
  const leads = detail?.leads ?? [];
  const phone = companyPhone(c);
  const loc = companyLocation(c);
  const stageMeta = STAGE_META[c.stage] ?? STAGE_META.prospect;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className={`flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-start justify-between gap-3 border-b p-5 ${tc.border}`}>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-building-line text-xl" />
            </span>
            <div>
              <h3 className={`text-lg font-bold ${tc.text}`}>{c.name}</h3>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${stageMeta.tone}`}>
                  {stageMeta.label}
                </span>
                {c.product && (
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${PRODUCT_TONE[c.product] ?? "bg-gray-400/15 text-gray-500"}`}>
                    {c.product}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className={`rounded-md p-1.5 ${tc.textSecondary} hover:bg-gray-500/10`}>
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Company fields */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <Field label="Industria" value={c.industry} tc={tc} />
            <Field label="Tamaño" value={c.size} tc={tc} />
            <Field label="Teléfono" value={phone} tc={tc} />
            <Field label="Estado / País" value={loc} tc={tc} />
            <Field label="Ciudad" value={c.city} tc={tc} />
            <Field label="Origen" value={c.source} tc={tc} />
            <div>
              <p className={`text-xs ${tc.textSecondary}`}>Sitio web</p>
              {companyWebsiteUrl(c) ? (
                <a
                  href={companyWebsiteUrl(c)!}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-0.5 inline-flex items-center gap-1 break-all text-sm text-primary-500 hover:underline"
                >
                  {companyWebsiteLabel(c)}
                  <i className="ri-external-link-line text-[11px] opacity-70" />
                </a>
              ) : (
                <p className={`mt-0.5 break-words text-sm ${tc.text}`}>—</p>
              )}
            </div>
            <Field label="Dirección" value={c.prospect?.address ?? null} tc={tc} className="col-span-2 sm:col-span-3" />
          </div>

          {/* Contact people */}
          <div className="mt-6">
            <div className="mb-2 flex items-center justify-between">
              <h4 className={`text-sm font-bold ${tc.text}`}>
                Personas de contacto
                <span className={`ml-2 text-xs font-normal ${tc.textSecondary}`}>({leads.length})</span>
              </h4>
              <button
                onClick={() => onAddPerson(c)}
                className="inline-flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-emerald-600"
              >
                <i className="ri-user-add-line" />
                Agregar persona
              </button>
            </div>

            {loading ? (
              <div className={`p-6 text-center text-sm ${tc.textSecondary}`}>
                <i className="ri-loader-4-line mr-2 animate-spin" />
                Cargando contactos...
              </div>
            ) : error ? (
              <p className="p-4 text-center text-sm text-red-500">{error}</p>
            ) : leads.length === 0 ? (
              <div className={`rounded-xl border border-dashed p-6 text-center text-sm ${tc.border} ${tc.textSecondary}`}>
                Aún no hay personas registradas en esta empresa.
              </div>
            ) : (
              <div className="space-y-2">
                {leads.map((l) => (
                  <ContactRow key={l.id} lead={l} tc={tc} isDarkMode={isDarkMode} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// A single contact person (lead) row inside the company detail panel.
// Exported for unit tests (see EmpresasLive.test.tsx).
export function ContactRow({
  lead,
  tc,
  isDarkMode,
}: {
  lead: Lead;
  tc: ReturnType<typeof buildThemeClasses>;
  isDarkMode: boolean;
}) {
  // A lead's number lives in `whatsapp` OR in `phone` (the landing asks whether
  // the line has WhatsApp), so read it through the shared helper — otherwise a
  // phone-only contact shows up here as "Sin datos de contacto".
  const phone = leadPhone(lead);
  const whatsappLine = hasWhatsappLine(lead);
  return (
    <div className={`flex items-center justify-between gap-3 rounded-xl border p-3 ${tc.border} ${isDarkMode ? "bg-[#1C1C1C]" : "bg-gray-50"}`}>
      <div className="min-w-0">
        <p className={`truncate text-sm font-semibold ${tc.text}`}>
          {lead.name || "—"}
          {lead.role && <span className={`ml-2 text-xs font-normal ${tc.textSecondary}`}>· {lead.role}</span>}
        </p>
        <p className={`mt-0.5 truncate text-xs ${tc.textSecondary}`}>
          {[phone, lead.email].filter(Boolean).join(" · ") || "Sin datos de contacto"}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {phone && whatsappLine && (
          <a
            title="WhatsApp"
            href={`https://wa.me/${phone.replace(/[^\d]/g, "")}`}
            target="_blank"
            rel="noreferrer"
            className="rounded-md p-1.5 text-emerald-500 hover:bg-emerald-500/10"
          >
            <i className="ri-whatsapp-line" />
          </a>
        )}
        {/* Sin WhatsApp: se marca, no se abre wa.me/ (el número no lo recibiría). */}
        {phone && !whatsappLine && (
          <a
            title="Llamar (sin WhatsApp)"
            href={`tel:${phone.replace(/[^\d+]/g, "")}`}
            className="rounded-md p-1.5 text-sky-500 hover:bg-sky-500/10"
          >
            <i className="ri-phone-line" />
          </a>
        )}
        {lead.email && (
          <a
            title="Correo"
            href={`mailto:${lead.email}`}
            className="rounded-md p-1.5 text-primary-500 hover:bg-primary-500/10"
          >
            <i className="ri-mail-line" />
          </a>
        )}
      </div>
    </div>
  );
}

// Label + value pair for the company detail grid.
function Field({
  label,
  value,
  tc,
  className = "",
}: {
  label: string;
  value: string | null | undefined;
  tc: ReturnType<typeof buildThemeClasses>;
  className?: string;
}) {
  return (
    <div className={className}>
      <p className={`text-xs ${tc.textSecondary}`}>{label}</p>
      <p className={`mt-0.5 break-words text-sm ${tc.text}`}>{value || "—"}</p>
    </div>
  );
}

/** Compañía de la plataforma de una Empresa del CRM: estado + etapa de su negocio. */
function PlatformCell({ platform }: { platform: PlatformCommercial | null }) {
  if (!platform) return <span className="text-gray-400">—</span>;
  const stage = platform.dealStage ? STAGE_BY_ID[platform.dealStage as DealStage]?.label ?? platform.dealStage : null;
  return (
    <span className="flex flex-col gap-0.5">
      <span className="font-medium">{platform.name || platform.id}</span>
      <span className="flex items-center gap-1">
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${BILLING_META[platform.billingClass].tone}`}>
          {billingLabel(platform)}
        </span>
        {stage && <span className="text-[11px] text-gray-500">Negocio: {stage}</span>}
      </span>
    </span>
  );
}
